import axiosBase from "../../utils/axios/axios";
import direcciónZonaUrbana from "../../utils/direcciónZonaUrbana";
import {
    getPersonInfo,
    getPersonalDetail,
    getResidenceInfo,
    requestDetails,
} from "./getterFormsData/getDataPageOne";
import {
    getNivelSocioeconomico,
    getSistemaSalud,
} from "./getterFormsData/getDataPageTwo";
import {
    getArtisticExp,
    getTrainingList,
    getTrainings,
    getUpdateArtisticExp,
    getUpdateTrainingList,
    getUpdateTrainings,
} from "./getterFormsData/getDataPageThree";
import { getSituacionLaboral } from "./getterFormsData/getDataPageFour";
import { getContacto } from "./getterFormsData/getDataFive";
import { getDataPageSix } from "./getterFormsData/getDataPageSix";
import Swal from "sweetalert2";
import { encode as base64_encode } from "base-64";
import { getEnvVariables } from "../../utils/helpers/getEnvVariables";

const { API_URL } = getEnvVariables();

const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
        toast.addEventListener("mouseenter", Swal.stopTimer);
        toast.addEventListener("mouseleave", Swal.resumeTimer);
    },
});

const getPersonID = () => localStorage.getItem("persona_id");

/*   Pagina 1   */
/**
 * The function `sendFormPageOne` sends form data to a server endpoint and stores the resulting persona
 * ID in local storage.
 * @param values - The `values` parameter in the `sendFormPageOne` function likely contains data or
 * information related to a form submission on a webpage. It seems to include details such as personal
 * information, residence information, and request details. The function processes this data, sends a
 * POST request to a specific endpoint ("/
 * @returns The function `sendFormPageOne` is returning a boolean value - `true` if the form data was
 * successfully sent and `false` if there was an error during the process.
 */
export const sendFormPageOne = async (values) => {
    values.direcciónGenerada = direcciónZonaUrbana(values);
    const postData = {
        persona: getPersonInfo(values),
        datos_personales: getPersonalDetail(values),
        residencia: getResidenceInfo(values),
        solicitud: requestDetails(values),
    };
    try {
        const { data } = await axiosBase.post("/personas/paso-uno/", postData);
        const { persona_id } = data;
        localStorage.setItem("persona_id", persona_id);
        return true;
    } catch (error) {
        return false;
    }
};

/**
 * This function fetches data from a specific endpoint based on a provided DNI number.
 * @param dni - The `dni` parameter in the `getDataFormPageOne` function is a unique identifier for a
 * person, typically used in Spanish-speaking countries for identification purposes.
 * @returns The function `getDataFormPageOne` is returning the data fetched from the API endpoint
 * `personas/paso-uno/consulta//` if the request is successful. If there is an error during the
 * request, it will return `false`.
 */
export const getDataFormPageOne = async (dni) => {
    try {
        const { data } = await axiosBase.get(
            `personas/paso-uno/consulta/${dni}/`
        );
        const idBarrio = data?.residencia?.barrio;
        if (idBarrio) {
            const infoBarrio = await axiosBase.get(`utils/barrios/${idBarrio}`);
            const idLocalidad = infoBarrio.data.localidad;
            data.residencia.localidad = idLocalidad;
        } else {
            data.residencia.localidad = data?.residencia?.localidad_rural;
        }
        return data;
    } catch (error) {
        return false;
    }
};

/**
 * The function `updateFormPageOne` updates form data for a specific person on a web application.
 * @param [values] - The `values` parameter in the `updateFormPageOne` function seems to be an object
 * containing various form data. It is used to generate different pieces of information needed for
 * updating a form on a web application. The function updates the form data by sending a PATCH request
 * to a specific endpoint with the
 * @returns The `updateFormPageOne` function is returning a boolean value - `true` if the axios request
 * is successful and `false` if there is an error caught in the try-catch block.
 */
export const updateFormPageOne = async (values = {}) => {
    values.direcciónGenerada = direcciónZonaUrbana(values);
    const postData = {
        persona: getPersonInfo(values),
        datos_personales: getPersonalDetail(values),
        residencia: getResidenceInfo(values),
        solicitud: requestDetails(values),
    };
    try {
        const { data } = await axiosBase.patch(
            `personas/paso-uno-patch/${getPersonID()}/`,
            postData
        );
        return true;
    } catch (error) {
        return false;
    }
};

/*   Pagina 2   */
/**
 * The function `sendFormPageTwo` sends data from a form to a server endpoint for the second step of a
 * multi-step form.
 * @param values - The `values` parameter in the `sendFormPageTwo` function likely contains data from a
 * form submission on a webpage. It could include information such as user input related to
 * socioeconomic level, health system preferences, and other relevant details for the second step of a
 * form submission process.
 * @returns If the POST request is successful, the function will return `true`. If there is an error
 * during the request, nothing will be returned explicitly (implicitly `undefined` will be returned).
 */
export const sendFormPageTwo = async (values) => {
    const postData = {
        nivel_socioeconomico: getNivelSocioeconomico(values),
        sistema_salud: getSistemaSalud(values),
        id_persona: getPersonID(),
    };
    try {
        const { data } = await axiosBase.post("/personas/paso-dos/", postData);
        return true;
    } catch (error) {
        return;
    }
};

/**
 * This function fetches data from a specific endpoint based on a provided DNI and returns the data or
 * false if an error occurs.
 * @param dni - The `dni` parameter in the `getDataFormPageTwo` function is a unique identifier for a
 * person, often used in Spanish-speaking countries. It stands for "Documento Nacional de Identidad"
 * which translates to National Identity Document.
 * @returns The function `getDataFormPageTwo` is returning the data fetched from the API endpoint
 * `personas/paso-dos/consulta//` if the request is successful. If there is an error during the
 * request, it will return `false`.
 */
export const getDataFormPageTwo = async (dni) => {
    try {
        const { data } = await axiosBase.get(
            `personas/paso-dos/consulta/${dni}/`
        );
        return data;
    } catch (error) {
        return false;
    }
};

/**
 * The function `updateFormPageTwo` updates specific data for a person in a form on page two using an
 * HTTP PATCH request.
 * @param [values] - The `values` parameter in the `updateFormPageTwo` function likely contains data
 * related to a form submission on a webpage. It seems to be an object that holds various form field
 * values or user input. The function uses this data to construct a `postData` object that includes
 * specific fields such as
 * @returns The `updateFormPageTwo` function is returning a boolean value. If the axios request is
 * successful, it will return `true`. If there is an error during the request, it will return `false`.
 */
export const updateFormPageTwo = async (values = {}) => {
    const postData = {
        nivel_socioeconomico: getNivelSocioeconomico(values),
        sistema_salud: getSistemaSalud(values),
        id_persona: getPersonID(),
    };
    try {
        const { data } = await axiosBase.patch(
            `personas/paso-dos-patch/${getPersonID()}/`,
            postData
        );
        return true;
    } catch (error) {
        return false;
    }
};

/*   Pagina 3   */
/**
 * The function `sendFormPageThree` sends form data related to a person's training and artistic
 * experience to a server endpoint.
 * @param values - The `values` parameter in the `sendFormPageThree` function likely contains data
 * related to a form submission on a webpage. This data could include information entered by a user
 * such as training details, artistic experience, and artistic/cultural training list.
 * @returns The `sendFormPageThree` function is returning a boolean value. It returns `true` if the
 * POST request to "/personas/paso-tres/" is successful, and `false` if there is an error during the
 * request.
 */
export const sendFormPageThree = async (values) => {
    const postData = {
        id_persona: getPersonID(),
        formacion: getTrainings(values),
        experiencia_artistica: getArtisticExp(values),
        formacion_artisticacultural: getTrainingList(values),
    };
    try {
        await axiosBase.post("/personas/paso-tres/", postData);
        return true;
    } catch (error) {
        return false;
    }
};

/**
 * The function `getDataFormPageThree` makes an asynchronous request to retrieve data from a specific
 * endpoint based on a provided DNI.
 * @param dni - The `dni` parameter in the `getDataFormPageThree` function is a unique identifier for a
 * person, often used in Spanish-speaking countries for identification purposes. It stands for
 * "Documento Nacional de Identidad" which translates to National Identity Document.
 * @returns The function `getDataFormPageThree` is returning the data fetched from the API endpoint
 * `personas/paso-tres/consulta//` after making a GET request using axios. If the request is
 * successful, it will return the retrieved data. If there is an error during the request, it will log
 * the error and return `false`.
 */
export const getDataFormPageThree = async (dni) => {
    try {
        const { data } = await axiosBase.get(
            `personas/paso-tres/consulta/${dni}/`
        );

        return {
            ...data,
            tiempoActividadAnios: parseInt(
                parseInt(data.experiencia_artistica.tiempo_dedicado_actividad) /
                    12
            ),
            tiempoActividadMeses:
                parseInt(data.experiencia_artistica.tiempo_dedicado_actividad) %
                12,
        };
    } catch (error) {
        console.error(error);
        return false;
    }
};

/**
 * The function `updateFormPageThree` updates a user's form data related to training, artistic
 * experience, and cultural training using a PATCH request.
 * @param [values] - The `values` parameter in the `updateFormPageThree` function likely contains data
 * related to a form submission or user input. This data is used to update various fields in a person's
 * profile, specifically related to their training, artistic experience, and artistic-cultural
 * training.
 * @returns The function `updateFormPageThree` is returning a boolean value. It returns `true` if the
 * axios request is successful and `false` if there is an error during the request.
 */
export const updateFormPageThree = async (values = {}) => {
    const postData = {
        id_persona: getPersonID(),
        formacion: getUpdateTrainings(values),
        experiencia_artistica: getUpdateArtisticExp(values),
        formacion_artisticacultural: getUpdateTrainingList(values),
    };
    try {
        const { data } = await axiosBase.patch(
            `personas/paso-tres-patch/${getPersonID()}/`,
            postData
        );
        return true;
    } catch (error) {
        return false;
    }
};

/*   Pagina 4   */
/**
 * The function `sendFormPageFour` sends data from a form to a specific endpoint and returns true if
 * successful, false otherwise.
 * @param values - The `values` parameter in the `sendFormPageFour` function likely contains data
 * related to a form submission on page four of a multi-step form. This data could include information
 * entered by the user such as employment status, work experience, or any other relevant details for
 * the specific step of the form
 * @returns The `sendFormPageFour` function is returning a boolean value. It returns `true` if the POST
 * request is successful and `false` if there is an error during the request.
 */
export const sendFormPageFour = async (values) => {
    const postData = {
        id_persona: getPersonID(),
        situacion_laboral: getSituacionLaboral(values),
    };
    try {
        await axiosBase.post("/personas/paso-cuatro/", postData);
        return true;
    } catch (error) {
        return false;
    }
};

/**
 * This function fetches data from a specific endpoint related to step four of a form using a provided
 * DNI.
 * @param dni - The `dni` parameter in the `getDataFormPageFour` function is a unique identifier for a
 * person, often used in Spanish-speaking countries to refer to a national identification number. It is
 * typically a string or number that uniquely identifies an individual in a database or system.
 * @returns The function `getDataFormPageFour` is returning the data fetched from the API endpoint
 * `personas/paso-cuatro/consulta//` if the request is successful. If there is an error during
 * the API call, it will return `false`.
 */
export const getDataFormPageFour = async (dni) => {
    try {
        const { data } = await axiosBase.get(
            `personas/paso-cuatro/consulta/${dni}/`
        );
        return data;
    } catch (error) {
        return false;
    }
};

/**
 * The function `updateFormPageFour` updates the employment status of a person in a form on a specific
 * page.
 * @param [values] - The `values` parameter in the `updateFormPageFour` function likely contains data
 * related to a form submission on page four of a multi-step form. This data could include information
 * such as the user's employment situation or any other relevant details needed to update the form for
 * a specific user.
 * @returns The `updateFormPageFour` function is returning a boolean value. It returns `true` if the
 * axios request is successful and `false` if there is an error during the request.
 */
export const updateFormPageFour = async (values = {}) => {
    const postData = {
        id_persona: getPersonID(),
        situacion_laboral: getSituacionLaboral(values),
    };
    try {
        const { data } = await axiosBase.patch(
            `personas/paso-cuatro-patch/${getPersonID()}/`,
            postData
        );
        return true;
    } catch (error) {
        return false;
    }
};

/*   Pagina 5   */
/**
 * The function `sendFormPageFive` sends form data to a specific endpoint and handles errors related to
 * creating a user.
 * @param values - The `values` parameter in the `sendFormPageFive` function likely contains the form
 * data submitted by the user on page five of a multi-step form. This data is used to construct the
 * `postData` object that includes the `id_persona` (person ID) and `contacto` (
 * @returns The function `sendFormPageFive` returns `true` if the form data is successfully sent and
 * the ID of the created request is stored in the local storage. If there is an error during the
 * process, it checks for a specific error message related to using an email associated with another
 * request and returns a corresponding message. If none of these conditions are met, it returns
 * `false`.
 */
export const sendFormPageFive = async (values) => {
    const postData = {
        id_persona: getPersonID(),
        contacto: getContacto(values),
    };
    try {
        const { data, ...response } = await axiosBase.post(
            "/personas/paso-cinco/",
            postData
        );
        localStorage.setItem("id_solicitud", data.solicitud);
        return true;
    } catch (error) {
        if (error.response?.data) {
            const html = error.response.data;
            const regex = /Error\s+desconocido\s+al\s+crear\s+el\s+usuario/i;
            const resultado = regex.test(html);
            if (resultado) {
                return "No puede usar este correo porque está asociado a otra solicitud";
            }
        }
        return false;
    }
};

/**
 * This function retrieves data from page five using a provided DNI number.
 * @param dni - The `dni` parameter in the `getDataFormPageFive` function is a unique identifier for a
 * person, often used in Spanish-speaking countries to refer to a national identification number. It is
 * typically a string or number that uniquely identifies an individual in a database or system.
 * @returns The function `getDataFormPageFive` is returning the data fetched from the API endpoint
 * `personas/paso-cinco/consulta//` if the request is successful. If there is an error during the
 * API call, it will return `false`.
 */
export const getDataFormPageFive = async (dni) => {
    try {
        const { data } = await axiosBase.get(
            `personas/paso-cinco/consulta/${dni}/`
        );
        return data;
    } catch (error) {
        return false;
    }
};

/**
 * The function `updateFormPageFive` updates a specific person's contact information on page five of a
 * form.
 * @param [values] - The `values` parameter in the `updateFormPageFive` function likely contains data
 * related to a form submission on page five of a multi-step form. This data could include information
 * entered by the user such as contact details, preferences, or any other relevant information for the
 * form.
 * @returns The `updateFormPageFive` function is returning a boolean value. It returns `true` if the
 * axios request is successful and `false` if there is an error during the request.
 */
export const updateFormPageFive = async (values = {}) => {
    const postData = {
        id_persona: getPersonID(),
        contacto: getContacto(values),
    };
    try {
        const { data } = await axiosBase.patch(
            `personas/paso-cinco-patch/${getPersonID()}/`,
            postData
        );
        return true;
    } catch (error) {
        return false;
    }
};

/*  Pagina 6 */
/**
 * The function `getDataFormPageSix` retrieves specific data and files based on a given document number
 * and organizes them into different categories.
 * @param dni - The function `getDataFormPageSix` is an asynchronous function that fetches data from an
 * API endpoint based on a provided DNI (Documento Nacional de Identidad) number. It retrieves specific
 * data and files related to the provided DNI, processes the data, and returns an object containing
 * various types of
 * @returns The function `getDataFormPageSix` is returning an object `dataArchives` which contains
 * various document types along with their details such as name, type, size, causal, and download URL.
 * The object includes document types like documentoDeIdentificación, declaraciónJuramentadaTerceros,
 * certificadoResidencia, certificadoAfiliaciónEps, soportesDeSocialización,
 */
export const getDataFormPageSix = async (dni) => {
    try {
        const { data } = await axiosBase.get(
            `personas/consulta-datos/?numero_documento=${dni}`
        );
        const datos_devolucion = data.results[0].datos_devolucion;
        const datos_archivos = Object.values(
            data.results[0].datos_archivos
                .filter((x) => x.tipo_archivo.estado == 1)
                .filter((x) => x.ruta_archivo)
                .reduce((acc, file) => {
                    const tipoCodigo = file.tipo_archivo.id;

                    if (!acc[tipoCodigo]) {
                        acc[tipoCodigo] = file;
                    } else {
                        if (
                            new Date(file.fecha_carga) >
                            new Date(acc[tipoCodigo].fecha_carga)
                        ) {
                            acc[tipoCodigo] = file;
                        }
                    }

                    return acc;
                }, {})
        ).map((x) => {
            return {
                ...x,
                anexo_codigo:
                    x.anexo_codigo === null
                        ? x.ruta_archivo?.split("/").pop().split(".").shift()
                        : x.anexo_codigo,
            };
        });

        let documentoDeIdentificación;
        let declaraciónJuramentadaTerceros;
        let certificadoResidencia;
        let certificadoAfiliaciónEps;
        let soportesDeSocialización;
        let premios;
        let certificaciónEventos;
        let certificadoSociedadColectiva;
        let certificadoContratos;
        let otrosSoportesTrayectoria;
        let tarjetaProfesional;
        let certificadoRegistroObras;
        let soporteTrayectoriaGestor;
        let otrosSoportesGestor;
        let certificaciónDiscapacidad;
        let declaraciónJuramentadaAdultoMayor;
        let certificaciónSisben;
        let soporteListadoCensal;
        let certificaciónAgremiación;
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 78) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 78
            )
        ) {
            documentoDeIdentificación = [
                {
                    lastModified: 0,
                    name:
                        datos_archivos.find((x) => x.tipo_archivo.id === 78)
                            ?.anexo_codigo !== null
                            ? `Cédula_de_ciudadanía_${
                                  datos_archivos.find(
                                      (x) => x.tipo_archivo.id === 78
                                  )?.anexo_codigo ?? ""
                              }.pdf`
                            : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 78
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 78)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 79) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 79
            )
        ) {
            declaraciónJuramentadaTerceros = [
                {
                    lastModified: 0,
                    name: datos_archivos.find((x) => x.tipo_archivo.id === 79)
                        ?.anexo_codigo
                        ? `declaración_juramentada_terceros_${
                              datos_archivos.find(
                                  (x) => x.tipo_archivo.id === 79
                              )?.anexo_codigo ?? ""
                          }.pdf`
                        : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 79
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 79)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 80) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 80
            )
        ) {
            certificadoResidencia = [
                {
                    lastModified: 0,
                    name: datos_archivos.find((x) => x.tipo_archivo.id === 80)
                        ?.anexo_codigo
                        ? `certificado_residencia_${
                              datos_archivos.find(
                                  (x) => x.tipo_archivo.id === 80
                              )?.anexo_codigo ?? ""
                          }.pdf`
                        : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 80
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 80)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 81) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 81
            )
        ) {
            certificadoAfiliaciónEps = [
                {
                    lastModified: 0,
                    name: datos_archivos.find((x) => x.tipo_archivo.id === 81)
                        ?.anexo_codigo
                        ? `certificado_afiliación_eps_${
                              datos_archivos.find(
                                  (x) => x.tipo_archivo.id === 81
                              )?.anexo_codigo ?? ""
                          }.pdf`
                        : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 81
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 81)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 82) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 82
            )
        ) {
            soportesDeSocialización = [
                {
                    lastModified: 0,
                    name: datos_archivos.find((x) => x.tipo_archivo.id === 82)
                        ?.anexo_codigo
                        ? `soportes_de_socialización_${
                              datos_archivos.find(
                                  (x) => x.tipo_archivo.id === 82
                              )?.anexo_codigo ?? ""
                          }.pdf`
                        : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 82
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 82)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 83) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 83
            )
        ) {
            premios = [
                {
                    lastModified: 0,
                    name: datos_archivos.find((x) => x.tipo_archivo.id === 83)
                        ?.anexo_codigo
                        ? `premios_${
                              datos_archivos.find(
                                  (x) => x.tipo_archivo.id === 83
                              )?.anexo_codigo ?? ""
                          }.pdf`
                        : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 83
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 83)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 84) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 84
            )
        ) {
            certificaciónEventos = [
                {
                    lastModified: 0,
                    name: datos_archivos.find((x) => x.tipo_archivo.id === 84)
                        ?.anexo_codigo
                        ? `certificación_eventos_${
                              datos_archivos.find(
                                  (x) => x.tipo_archivo.id === 84
                              )?.anexo_codigo ?? ""
                          }.pdf`
                        : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 84
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 84)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 465) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 465
            )
        ) {
            certificadoSociedadColectiva = [
                {
                    lastModified: 0,
                    name: datos_archivos.find((x) => x.tipo_archivo.id === 465)
                        ?.anexo_codigo
                        ? `certificado_sociedad_colectiva_${
                              datos_archivos.find(
                                  (x) => x.tipo_archivo.id === 465
                              )?.anexo_codigo ?? ""
                          }.pdf`
                        : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 465
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 465)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 466) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 466
            )
        ) {
            certificadoContratos = [
                {
                    lastModified: 0,
                    name: datos_archivos.find((x) => x.tipo_archivo.id === 466)
                        ?.anexo_codigo
                        ? `certificado_contratos_${
                              datos_archivos.find(
                                  (x) => x.tipo_archivo.id === 466
                              )?.anexo_codigo ?? ""
                          }.pdf`
                        : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 466
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 466)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 467) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 467
            )
        ) {
            otrosSoportesTrayectoria = [
                {
                    lastModified: 0,
                    name: datos_archivos.find((x) => x.tipo_archivo.id === 467)
                        ?.anexo_codigo
                        ? `otros_soportes_trayectoria_${
                              datos_archivos.find(
                                  (x) => x.tipo_archivo.id === 467
                              )?.anexo_codigo ?? ""
                          }.pdf`
                        : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 467
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 467)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 468) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 468
            )
        ) {
            tarjetaProfesional = [
                {
                    lastModified: 0,
                    name: datos_archivos.find((x) => x.tipo_archivo.id === 468)
                        ?.anexo_codigo
                        ? `tarjeta_profesional_${
                              datos_archivos.find(
                                  (x) => x.tipo_archivo.id === 468
                              )?.anexo_codigo ?? ""
                          }.pdf`
                        : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 468
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 468)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 464) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 464
            )
        ) {
            certificadoRegistroObras = [
                {
                    lastModified: 0,
                    name: datos_archivos.find((x) => x.tipo_archivo.id === 464)
                        ?.anexo_codig
                        ? `certificado_registro_obras_${
                              datos_archivos.find(
                                  (x) => x.tipo_archivo.id === 464
                              )?.anexo_codigo ?? ""
                          }.pdf`
                        : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 464
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 464)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 462) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 462
            )
        ) {
            soporteTrayectoriaGestor = [
                {
                    lastModified: 0,
                    name: datos_archivos.find((x) => x.tipo_archivo.id === 462)
                        ?.anexo_codigo
                        ? `soporte_trayectoria_gestor_${
                              datos_archivos.find(
                                  (x) => x.tipo_archivo.id === 462
                              )?.anexo_codigo ?? ""
                          }.pdf`
                        : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 462
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 462)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 463) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 463
            )
        ) {
            otrosSoportesGestor = [
                {
                    lastModified: 0,
                    name: datos_archivos.find((x) => x.tipo_archivo.id === 463)
                        ?.anexo_codigo
                        ? `otros_soportes_gestor_${
                              datos_archivos.find(
                                  (x) => x.tipo_archivo.id === 463
                              )?.anexo_codigo ?? ""
                          }.pdf`
                        : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 463
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 463)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 469) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 469
            )
        ) {
            certificaciónDiscapacidad = [
                {
                    lastModified: 0,
                    name: datos_archivos.find((x) => x.tipo_archivo.id === 469)
                        ?.anexo_codigo
                        ? `certificación_discapacidad_${
                              datos_archivos.find(
                                  (x) => x.tipo_archivo.id === 469
                              )?.anexo_codigo ?? ""
                          }.pdf`
                        : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 469
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 469)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 470) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 470
            )
        ) {
            declaraciónJuramentadaAdultoMayor = [
                {
                    lastModified: 0,
                    name: datos_archivos.find((x) => x.tipo_archivo.id === 470)
                        ?.anexo_codigo
                        ? `declaración_juramentada_adulto_mayor_${
                              datos_archivos.find(
                                  (x) => x.tipo_archivo.id === 470
                              )?.anexo_codigo ?? ""
                          }.pdf`
                        : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 470
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 470)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 471) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 471
            )
        ) {
            certificaciónSisben = [
                {
                    lastModified: 0,
                    name: datos_archivos.find((x) => x.tipo_archivo.id === 471)
                        ?.anexo_codigo
                        ? `certificación_sisben_${
                              datos_archivos.find(
                                  (x) => x.tipo_archivo.id === 471
                              )?.anexo_codigo ?? ""
                          }.pdf`
                        : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 471
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 471)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 472) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 472
            )
        ) {
            soporteListadoCensal = [
                {
                    lastModified: 0,
                    name: datos_archivos.find((x) => x.tipo_archivo.id === 472)
                        ?.anexo_codigo
                        ? `soporte_listado_censal_${
                              datos_archivos.find(
                                  (x) => x.tipo_archivo.id === 472
                              )?.anexo_codigo ?? ""
                          }.pdf`
                        : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 472
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 472)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        if (
            datos_archivos.some((x) => x.tipo_archivo.id === 473) ||
            datos_devolucion?.some(
                (x) => x.causal_devolucion.tipo_documento === 473
            )
        ) {
            certificaciónAgremiación = [
                {
                    lastModified: 0,
                    name: datos_archivos.find((x) => x.tipo_archivo.id === 473)
                        ?.anexo_codigo
                        ? `certificación_agremiación_${
                              datos_archivos.find(
                                  (x) => x.tipo_archivo.id === 473
                              )?.anexo_codigo ?? ""
                          }.pdf`
                        : "Sin Archivo",
                    type: "application/pdf",
                    size: 0,
                    causal: datos_devolucion?.find(
                        (x) => x.causal_devolucion.tipo_documento === 473
                    )?.causal_devolucion.causal_devolucion,
                    url: `${API_URL}/solicitudes/descargar-archivo/${base64_encode(
                        datos_archivos.find((x) => x.tipo_archivo.id === 473)
                            ?.ruta_archivo
                    )}`,
                },
            ];
        }
        const dataArchives = {
            documentoDeIdentificación,
            declaraciónJuramentadaTerceros,
            certificadoResidencia,
            certificadoAfiliaciónEps,
            soportesDeSocialización,
            premios,
            certificaciónEventos,
            certificadoSociedadColectiva,
            certificadoContratos,
            otrosSoportesTrayectoria,
            tarjetaProfesional,
            certificadoRegistroObras,
            soporteTrayectoriaGestor,
            otrosSoportesGestor,
            certificaciónDiscapacidad,
            declaraciónJuramentadaAdultoMayor,
            certificaciónSisben,
            soporteListadoCensal,
            certificaciónAgremiación,
        };

        return dataArchives;
    } catch (error) {
        return false;
    }
};

/**
 * The function `sendFormPageSix` sends form data including files to a server endpoint and returns a
 * success message with a reference number or handles errors.
 * @param values - The `values` parameter in the `sendFormPageSix` function likely contains data or
 * information related to the form being submitted on page six. This data is used to extract a list of
 * files in the `getDataPageSix` function and then construct a `FormData` object (`postForm`) to
 * @returns The function `sendFormPageSix` is returning an object with two properties:
 * - `success`: a boolean value indicating whether the form submission was successful or not
 * - `radicado`: the radicado value obtained from the response data if the submission was successful
 */
export const sendFormPageSix = async (values) => {
    const fileList = getDataPageSix(values);
    const postForm = new FormData();
    const id_solicitud = localStorage.id_solicitud;

    postForm.append("id_solicitud", id_solicitud);
    fileList.forEach((item) => {
        postForm.append("files", item.file);
        postForm.append("id_tipo_archivo", item.id);
    });

    try {
        const response = await axiosBase.post(
            "/solicitudes/solicitud-archivos-upload/",
            postForm,
            { headers: { "Content-Type": "multipart/form-data" } }
        );
        return {
            success: true,
            radicado: response.data.radicado,
            uuid: response.data.uuid,
        };
    } catch (error) {
        console.error(error);
        Toast.fire({
            icon: "error",
            title: "Error en el servidor al cargar los documentos, intentelo más tarde.",
        });
        return false;
    }
};

/**
 * The function `updateFormPageSix` is used to update a form by uploading files to a server using Axios
 * in JavaScript.
 * @param values - The `updateFormPageSix` function takes in a `values` object as a parameter. It
 * filters out any keys in the `values` object that have a value of type `FileList` and stores them in
 * a new object called `files`. It then calls the `getDataPageSix`
 * @returns The function `updateFormPageSix` is returning an object with two properties: `success` and
 * `radicado`. If the axios request is successful, `success` will be `true` and `radicado` will contain
 * the radicado value from the response data. If there is an error during the axios request, the
 * function will return `false`.
 */
/**
 * The function `updateFormPageSix` is used to update a form by uploading files to a server using Axios
 * in JavaScript.
 * @param values - The `updateFormPageSix` function is designed to update a form page with file
 * uploads. Here's a breakdown of the function:
 * @returns The function `updateFormPageSix` is returning an object with two properties:
 * - `success`: a boolean value indicating whether the operation was successful or not
 * - `radicado`: the radicado value obtained from the response data
 */
export const updateFormPageSix = async (values) => {
    const files = {};
    Object.keys(values)
        .filter((x) => values[x] instanceof FileList)
        .forEach((key) => {
            files[key] = values[key];
        });

    const fileList = getDataPageSix(files);

    const postForm = new FormData();
    const id_solicitud = localStorage.id_solicitud;

    postForm.append("id_solicitud", id_solicitud);
    fileList.forEach((item) => {
        postForm.append("files", item.file);
        postForm.append("id_tipo_archivo", item.id);
    });

    try {
        const response = await axiosBase.patch(
            `/solicitudes/solicitud-archivos-upload/${id_solicitud}`,
            postForm,
            { headers: { "Content-Type": "multipart/form-data" } }
        );
        localStorage.setItem("saveEdit", true);
        return { success: true, radicado: response.data.radicado };
    } catch (error) {
        console.error(error);
        Toast.fire({
            icon: "error",
            title: "Error en el servidor al cargar los documentos, intentelo más tarde.",
        });
        return false;
    }
};

/**
 * The `downloadFile` function downloads a file from a given URL, converts it to a base64 format, and
 * returns a promise with the base64 data.
 * @param url - The `url` parameter in the `downloadFile` function is the URL from which you want to
 * download a file. This function uses Axios to make a GET request to the specified URL and fetches the
 * file as an array buffer. It then converts the array buffer to a base64 string and returns
 * @returns The `downloadFile` function is returning a Promise that resolves with the base64
 * representation of the downloaded file (in this case, a PDF file).
 */
export const downloadFile = async (url) => {
    try {
        const response = await axiosBase.get(url, {
            responseType: "arraybuffer",
        });

        const arrayBuffer = response.data;
        const blob = new Blob([arrayBuffer], { type: "application/pdf" });
        const reader = new FileReader();

        return new Promise((resolve, reject) => {
            reader.onload = () => {
                const base64 = reader.result;
                resolve(base64);
            };

            reader.onerror = (error) => {
                reject(error);
            };

            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.error("Error al descargar y convertir a base64:", error);
    }
};

/**
 * The function `fileToBase64` converts a file to a base64 encoded string asynchronously using
 * FileReader in JavaScript.
 * @param file - The `file` parameter in the `fileToBase64` function is the file that you want to
 * convert to a base64 encoded string. This file can be an image, a document, or any other type of file
 * that can be read by the `FileReader` API in JavaScript.
 * @returns The `fileToBase64` function returns a Promise that resolves with the base64 representation
 * of the input file after a delay of 100 milliseconds.
 */
export const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const fileBase64 = reader.result?.toString();
            return setTimeout(() => resolve(fileBase64), 100);
        };
        reader.onerror = (error) => reject(error);
    });
};
